import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import IconButton from '@mui/material/IconButton';
import PersonIcon from '@mui/icons-material/Person';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import LanguageIcon from '@mui/icons-material/Language';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import WarningIcon from '@mui/icons-material/Warning';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import { useNavigate } from 'react-router-dom';
import { deleteLoyaltyAccount, updateLoyaltyProfile } from '../../services/publicUserApi';
import { loyaltySession } from '../../utils/session';
import { restoreLoyaltySession } from '../../utils/loyaltySessionRestore';
import { ApiError } from '../../utils/laravelError';
import './LoyaltySettings.css';

function LoyaltySettings() {
    const { t, i18n } = useTranslation();
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [successMessage, setSuccessMessage] = useState(null);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [editMode, setEditMode] = useState({ name: false, phone: false, preferred_language: false });
    const [editValues, setEditValues] = useState({ name: '', phone: '', preferred_language: 'en' });
    const [saving, setSaving] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        const fetchData = async () => {
            const storedUser = await restoreLoyaltySession({ forceValidate: true });

            if (!storedUser) {
                navigate('/loyalty/login');
                return;
            }

            try {
                setUser(storedUser);
                setEditValues({
                    name: storedUser.name || '',
                    phone: storedUser.phone || '',
                    preferred_language: storedUser.preferred_language || 'en',
                });
                setLoading(false);
            } catch (err) {
                console.error('Failed to parse user data:', err);
                setError(t('loyalty.profile.errors.loadUserData'));
                setLoading(false);
            }
        };

        fetchData();
    }, [navigate, t]);

    const handleEdit = (field) => {
        setEditMode({ ...editMode, [field]: true });
        setError(null);
        setSuccessMessage(null);
    };

    const handleCancel = (field) => {
        setEditMode({ ...editMode, [field]: false });
        setEditValues({ ...editValues, [field]: user[field] || '' });
    };

    const handleSave = async (field) => {
        setSaving(true);
        setError(null);
        setSuccessMessage(null);

        try {
            const updateData = { [field]: editValues[field] };
            const response = await updateLoyaltyProfile(updateData);

            const updatedUser = { ...user, ...(response?.user || updateData) };
            setUser(updatedUser);
            loyaltySession.setSession(updatedUser);

            // If updating language, change i18n language immediately
            if (field === 'preferred_language') {
                i18n.changeLanguage(editValues[field]);
            }

            setEditMode({ ...editMode, [field]: false });
            setSuccessMessage(t('loyalty.settings.updateSuccess', 'Информацията е актуализирана успешно'));

            // Clear success message after 3 seconds
            setTimeout(() => setSuccessMessage(null), 3000);
        } catch (err) {
            console.error('Failed to update profile:', err);
            if (err instanceof ApiError && err.status === 401) {
                navigate('/loyalty/login');
                return;
            }
            setError(err.message || t('loyalty.settings.updateError', 'Грешка при актуализиране'));
            setEditValues({ ...editValues, [field]: user[field] || '' });
        } finally {
            setSaving(false);
        }
    };

    const handleDeleteAccount = async () => {
        setDeleting(true);
        try {
            await deleteLoyaltyAccount();
            loyaltySession.clear();
            navigate('/loyalty/login', { state: { message: t('loyalty.profile.deleteSuccess') } });
        } catch (err) {
            console.error('Failed to delete account:', err);
            setError(t('loyalty.profile.errors.deleteFailed'));
            setDeleteModalOpen(false);
        } finally {
            setDeleting(false);
        }
    };

    if (loading) {
        return (
            <Box className="loyalty-settings-container">
                <CircularProgress sx={{ color: '#ff6b00' }} />
            </Box>
        );
    }

    if (!user) {
        return null;
    }

    return (
        <Box className="loyalty-settings-container">
            <Card className="loyalty-settings-card">
                <CardContent>
                    {/* Header */}
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, pb: 2, borderBottom: '1px solid rgba(255, 107, 0, 0.2)' }}>
                        <Typography variant="h5" sx={{ color: '#fff', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                            {t('loyalty.settings.title')}
                        </Typography>
                        <Button
                            startIcon={<ArrowBackIcon />}
                            onClick={() => navigate('/loyalty/profile')}
                            size="small"
                            sx={{
                                color: '#ff6b00',
                                borderColor: 'rgba(255, 107, 0, 0.3)',
                                '&:hover': {
                                    bgcolor: 'rgba(255, 107, 0, 0.1)',
                                    borderColor: '#ff6b00'
                                }
                            }}
                        >
                            {t('loyalty.settings.back')}
                        </Button>
                    </Box>

                    {/* Success Message */}
                    {successMessage && (
                        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage(null)}>
                            {successMessage}
                        </Alert>
                    )}

                    {/* Error Message */}
                    {error && (
                        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                            {error}
                        </Alert>
                    )}

                    {/* Personal Information */}
                    <Box className="settings-section">
                        <Typography variant="h6" sx={{ color: '#ff6b00', fontWeight: 600, mb: 2.5, fontSize: '1.1rem', textTransform: 'uppercase', letterSpacing: '1px' }}>
                            {t('loyalty.settings.personalInfo')}
                        </Typography>

                        <Box className="settings-details">
                            {/* Name Field */}
                            <Box className={`settings-item ${editMode.name ? 'editing' : ''}`}>
                                <PersonIcon sx={{ color: '#ff6b00', fontSize: 24 }} />
                                <Box>
                                    <Typography variant="caption">
                                        {t('loyalty.profile.name')}
                                    </Typography>
                                    {editMode.name ? (
                                        <>
                                            <TextField
                                                fullWidth
                                                value={editValues.name}
                                                onChange={(e) => setEditValues({ ...editValues, name: e.target.value })}
                                                variant="outlined"
                                                size="small"
                                                disabled={saving}
                                                placeholder={t('loyalty.profile.name')}
                                                sx={{
                                                    '& .MuiOutlinedInput-root': {
                                                        color: '#fff',
                                                        '& fieldset': { borderColor: 'rgba(255, 107, 0, 0.5)' },
                                                        '&:hover fieldset': { borderColor: '#ff6b00' },
                                                        '&.Mui-focused fieldset': { borderColor: '#ff6b00' }
                                                    }
                                                }}
                                            />
                                            <Box className="edit-actions">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleSave('name')}
                                                    disabled={saving || !editValues.name.trim()}
                                                    className="save-btn"
                                                >
                                                    {saving ? <CircularProgress size={20} /> : <SaveIcon />}
                                                </IconButton>
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleCancel('name')}
                                                    disabled={saving}
                                                    className="cancel-btn"
                                                >
                                                    <CancelIcon />
                                                </IconButton>
                                            </Box>
                                        </>
                                    ) : (
                                        <Typography variant="body2">
                                            {user.name}
                                        </Typography>
                                    )}
                                </Box>
                                {!editMode.name && (
                                    <IconButton
                                        size="small"
                                        onClick={() => handleEdit('name')}
                                        className="edit-btn"
                                    >
                                        <EditIcon fontSize="small" />
                                    </IconButton>
                                )}
                            </Box>

                            {/* Email Field (Read-only) */}
                            <Box className="settings-item">
                                <EmailIcon sx={{ color: '#ff6b00', fontSize: 24 }} />
                                <Box>
                                    <Typography variant="caption">
                                        {t('loyalty.profile.email')}
                                    </Typography>
                                    <Typography variant="body2">
                                        {user.email}
                                    </Typography>
                                </Box>
                            </Box>

                            {/* Phone Field */}
                            <Box className={`settings-item ${editMode.phone ? 'editing' : ''}`}>
                                <PhoneIcon sx={{ color: '#ff6b00', fontSize: 24 }} />
                                <Box>
                                    <Typography variant="caption">
                                        {t('loyalty.profile.phone')}
                                    </Typography>
                                    {editMode.phone ? (
                                        <>
                                            <TextField
                                                fullWidth
                                                value={editValues.phone}
                                                onChange={(e) => setEditValues({ ...editValues, phone: e.target.value })}
                                                variant="outlined"
                                                size="small"
                                                disabled={saving}
                                                placeholder={t('loyalty.auth.phone')}
                                                sx={{
                                                    '& .MuiOutlinedInput-root': {
                                                        color: '#fff',
                                                        '& fieldset': { borderColor: 'rgba(255, 107, 0, 0.5)' },
                                                        '&:hover fieldset': { borderColor: '#ff6b00' },
                                                        '&.Mui-focused fieldset': { borderColor: '#ff6b00' }
                                                    }
                                                }}
                                            />
                                            <Box className="edit-actions">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleSave('phone')}
                                                    disabled={saving}
                                                    className="save-btn"
                                                >
                                                    {saving ? <CircularProgress size={20} /> : <SaveIcon />}
                                                </IconButton>
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleCancel('phone')}
                                                    disabled={saving}
                                                    className="cancel-btn"
                                                >
                                                    <CancelIcon />
                                                </IconButton>
                                            </Box>
                                        </>
                                    ) : (
                                        <Typography variant="body2">
                                            {user.phone || t('loyalty.settings.noPhone', 'Не е посочен')}
                                        </Typography>
                                    )}
                                </Box>
                                {!editMode.phone && (
                                    <IconButton
                                        size="small"
                                        onClick={() => handleEdit('phone')}
                                        className="edit-btn"
                                    >
                                        <EditIcon fontSize="small" />
                                    </IconButton>
                                )}
                            </Box>

                            {/* Preferred Language Field */}
                            <Box className={`settings-item ${editMode.preferred_language ? 'editing' : ''}`}>
                                <LanguageIcon sx={{ color: '#ff6b00', fontSize: 24 }} />
                                <Box sx={{ flex: 1 }}>
                                    <Typography variant="caption">
                                        {t('loyalty.settings.preferredLanguage', 'Preferred Language')}
                                    </Typography>
                                    {editMode.preferred_language ? (
                                        <>
                                            <FormControl fullWidth size="small" sx={{ mt: 0.5 }}>
                                                <Select
                                                    value={editValues.preferred_language}
                                                    onChange={(e) => setEditValues({ ...editValues, preferred_language: e.target.value })}
                                                    disabled={saving}
                                                    sx={{
                                                        color: '#fff',
                                                        '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 107, 0, 0.5)' },
                                                        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#ff6b00' },
                                                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#ff6b00' },
                                                        '& .MuiSvgIcon-root': { color: '#ff6b00' }
                                                    }}
                                                >
                                                    <MenuItem value="en">English</MenuItem>
                                                    <MenuItem value="bg">Български</MenuItem>
                                                </Select>
                                            </FormControl>
                                            <Box className="edit-actions">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleSave('preferred_language')}
                                                    disabled={saving}
                                                    className="save-btn"
                                                >
                                                    {saving ? <CircularProgress size={20} /> : <SaveIcon />}
                                                </IconButton>
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleCancel('preferred_language')}
                                                    disabled={saving}
                                                    className="cancel-btn"
                                                >
                                                    <CancelIcon />
                                                </IconButton>
                                            </Box>
                                        </>
                                    ) : (
                                        <Typography variant="body2">
                                            {user.preferred_language === 'bg' ? 'Български' : 'English'}
                                        </Typography>
                                    )}
                                </Box>
                                {!editMode.preferred_language && (
                                    <IconButton
                                        size="small"
                                        onClick={() => handleEdit('preferred_language')}
                                        className="edit-btn"
                                    >
                                        <EditIcon fontSize="small" />
                                    </IconButton>
                                )}
                            </Box>
                        </Box>
                    </Box>

                    {/* Account Actions */}
                    <Box className="settings-section">
                        <Typography variant="h6" sx={{ color: '#ff6b00', fontWeight: 600, mb: 2.5, fontSize: '1.1rem', textTransform: 'uppercase', letterSpacing: '1px' }}>
                            {t('loyalty.settings.accountActions')}
                        </Typography>

                        <Button
                            variant="outlined"
                            fullWidth
                            startIcon={<DeleteForeverIcon />}
                            onClick={() => setDeleteModalOpen(true)}
                            sx={{
                                color: '#f44336',
                                borderColor: 'rgba(244, 67, 54, 0.5)',
                                padding: '12px 24px',
                                fontSize: '0.95rem',
                                fontWeight: 600,
                                borderRadius: '12px',
                                textTransform: 'none',
                                '&:hover': {
                                    borderColor: '#f44336',
                                    bgcolor: 'rgba(244, 67, 54, 0.1)',
                                    transform: 'translateY(-2px)',
                                    boxShadow: '0 4px 12px rgba(244, 67, 54, 0.2)'
                                },
                                transition: 'all 0.3s ease'
                            }}
                        >
                            {t('loyalty.profile.deleteAccount')}
                        </Button>
                    </Box>
                </CardContent>
            </Card>

            {/* Delete Account Confirmation Modal */}
            <Dialog
                open={deleteModalOpen}
                onClose={() => !deleting && setDeleteModalOpen(false)}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: {
                        bgcolor: 'rgba(30, 30, 30, 0.98)',
                        borderRadius: '16px',
                        border: '2px solid rgba(244, 67, 54, 0.3)'
                    }
                }}
            >
                <DialogTitle sx={{
                    color: '#f44336',
                    fontWeight: 600,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    borderBottom: '1px solid rgba(244, 67, 54, 0.2)'
                }}>
                    <WarningIcon sx={{ fontSize: 28 }} />
                    {t('loyalty.profile.deleteAccountTitle')}
                </DialogTitle>
                <DialogContent sx={{ padding: '2rem', color: '#fff' }}>
                    <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.6, color: 'rgba(255, 255, 255, 0.9)' }}>
                        {t('loyalty.profile.deleteAccountFarewell')}
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.6 }}>
                        {t('loyalty.profile.deleteAccountWarning')}
                    </Typography>
                    <Box sx={{
                        bgcolor: 'rgba(244, 67, 54, 0.1)',
                        border: '1px solid rgba(244, 67, 54, 0.3)',
                        borderRadius: '8px',
                        padding: '1rem',
                        mb: 2
                    }}>
                        <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.9)', mb: 1 }}>
                            {t('loyalty.profile.deleteAccountDetails')}
                        </Typography>
                        <ul style={{ margin: 0, paddingLeft: '1.5rem', color: 'rgba(255, 255, 255, 0.8)' }}>
                            <li>{t('loyalty.profile.deleteAccountItem1')}</li>
                            <li>{t('loyalty.profile.deleteAccountItem2')}</li>
                            <li>{t('loyalty.profile.deleteAccountItem3')}</li>
                        </ul>
                    </Box>
                    <Typography variant="body2" sx={{ color: '#f44336', fontWeight: 600 }}>
                        {t('loyalty.profile.deleteAccountConfirm')}
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ padding: '1rem 1.5rem', gap: 1 }}>
                    <Button
                        onClick={() => setDeleteModalOpen(false)}
                        variant="outlined"
                        disabled={deleting}
                        sx={{
                            borderColor: 'rgba(255, 255, 255, 0.3)',
                            color: '#fff',
                            '&:hover': {
                                borderColor: 'rgba(255, 255, 255, 0.5)',
                                bgcolor: 'rgba(255, 255, 255, 0.05)'
                            }
                        }}
                    >
                        {t('loyalty.profile.cancel')}
                    </Button>
                    <Button
                        onClick={handleDeleteAccount}
                        variant="contained"
                        disabled={deleting}
                        startIcon={deleting ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : <DeleteForeverIcon />}
                        sx={{
                            bgcolor: '#f44336',
                            color: '#fff',
                            '&:hover': {
                                bgcolor: '#d32f2f'
                            },
                            '&:disabled': {
                                bgcolor: 'rgba(244, 67, 54, 0.5)',
                                color: 'rgba(255, 255, 255, 0.5)'
                            }
                        }}
                    >
                        {deleting ? t('loyalty.profile.deleting') : t('loyalty.profile.confirmDelete')}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

export default LoyaltySettings;
